import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Alert, Box, Button, Checkbox, FormControlLabel, TextField } from '@mui/material'
import { authApi, mergeSubscriptionParameters } from '@/api/auth'
import { PasswordField } from '@/components/auth/PasswordField'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'
import { displayErrorMessage, getErrorMessage, getValidationErrors } from '@/utils/error'

/** Почему форма больше не нужна: ссылка истекла, использована или неизвестна */
export type CabinetActivationUnavailable = 'expired' | 'used' | 'invalid'

interface CabinetActivationFormProps {
  code: string
  /** Логин ученика из приглашения — только для чтения */
  email: string
  onUnavailable: (reason: CabinetActivationUnavailable) => void
}

// Правила пароля и согласий — те же, что при регистрации (RegisterForm)
const createActivationSchema = (t: (key: string) => string) =>
  z
    .object({
      password: z.string().min(8, t('auth.validation.passwordMin')),
      password_confirmation: z.string().min(8, t('auth.validation.passwordMin')),
      privacy_policy: z.boolean().refine((value) => value === true, {
        message: t('auth.validation.privacyPolicyRequired'),
      }),
      consent_to_mailing: z.boolean(),
    })
    .refine((data) => data.password === data.password_confirmation, {
      message: t('auth.validation.passwordMismatch'),
      path: ['password_confirmation'],
    })

type ActivationFormData = z.infer<ReturnType<typeof createActivationSchema>>

const SERVER_FIELDS = ['password', 'password_confirmation', 'privacy_policy'] as const

/**
 * Форма активации кабинета: пароль с подтверждением и согласия. После
 * активации — вход, как после регистрации: ученик попадает в кабинет по общим
 * правилам (`ProtectedRoute`, онбординг).
 */
export function CabinetActivationForm({ code, email, onUnavailable }: CabinetActivationFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const [error, setError] = useState<{ message: string; hint: string | null } | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const {
    control,
    handleSubmit,
    setError: setFieldError,
    formState: { errors },
  } = useForm<ActivationFormData>({
    resolver: zodResolver(createActivationSchema(t)),
    defaultValues: {
      password: '',
      password_confirmation: '',
      privacy_policy: false,
      consent_to_mailing: false,
    },
  })

  const onSubmit = async (data: ActivationFormData) => {
    setError(null)
    setIsLoading(true)
    try {
      const response = await authApi.activateCabinet(code, data)
      const user = await mergeSubscriptionParameters(response.user, response.token)
      setAuth(user, response.token, { expiresIn: response.expires_in, rememberMe: false })
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err: unknown) {
      const response = (err as { response?: { status?: number; data?: { error?: string } } }).response
      const reason = response?.data?.error

      // Ссылка успела устареть или сработать в другой вкладке — показываем это вместо формы
      if (response?.status === 404) {
        onUnavailable('invalid')
        return
      }
      if (reason === 'invitation_expired') {
        onUnavailable('expired')
        return
      }
      if (reason === 'invitation_used') {
        onUnavailable('used')
        return
      }

      const fieldErrors = getValidationErrors(err)
      for (const field of SERVER_FIELDS) {
        const message = fieldErrors?.[field]?.[0]
        if (message) setFieldError(field, { type: 'server', message })
      }

      setError({
        message: displayErrorMessage(getErrorMessage(err), t('cabinetActivation.error')),
        hint: reason === 'email_taken' ? t('cabinetActivation.emailTakenHint') : null,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
    >
      {/* Логин в той же форме: менеджер паролей сохранит пароль к этой почте */}
      <TextField
        label={t('cabinetActivation.emailLabel')}
        value={email}
        name="email"
        type="email"
        autoComplete="username"
        fullWidth
        InputProps={{ readOnly: true }}
        helperText={t('cabinetActivation.emailHint')}
      />

      <Controller
        name="password"
        control={control}
        render={({ field }) => (
          <PasswordField
            {...field}
            label={t('auth.password')}
            autoComplete="new-password"
            fullWidth
            error={!!errors.password}
            helperText={errors.password?.message}
          />
        )}
      />

      <Controller
        name="password_confirmation"
        control={control}
        render={({ field }) => (
          <PasswordField
            {...field}
            label={t('auth.passwordConfirmation')}
            autoComplete="new-password"
            fullWidth
            error={!!errors.password_confirmation}
            helperText={errors.password_confirmation?.message}
          />
        )}
      />

      <Controller
        name="privacy_policy"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox {...field} checked={field.value} color="primary" />}
            label={t('auth.privacyPolicyAccept')}
          />
        )}
      />
      {errors.privacy_policy && (
        <Alert severity="error" sx={{ mt: -2, mb: 1 }}>
          {errors.privacy_policy.message}
        </Alert>
      )}

      <Controller
        name="consent_to_mailing"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox {...field} checked={field.value} color="primary" />}
            label={t('auth.consentToMailing')}
          />
        )}
      />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error.message}
          {error.hint && <Box sx={{ mt: 0.5 }}>{error.hint}</Box>}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={isLoading}
        sx={{ mt: 1, py: 1.5 }}
      >
        {isLoading ? t('common.loading') : t('cabinetActivation.submit')}
      </Button>
    </Box>
  )
}
